import { DomainListItemCard } from "@/components/domain";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeletePayment } from "@/hooks/payments";
import { T } from "@/i18n";
import { dateToString } from "@/lib/utils";
import { Payment } from "@/models";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useGetReservationClientWithName } from "../-hooks/useGetReservationClientsWithName";

export function PaymentCard({
  payment,
  reservationId,
  isTable,
}: {
  payment: Payment;
  reservationId: string;
  isTable: boolean;
}) {
  const { data: client, isLoading: clientLoading } =
    useGetReservationClientWithName(reservationId, payment.payee.toString());
  const { mutateAsync } = useDeletePayment(reservationId);
  const deletePayment = () => mutateAsync(payment.id?.toString() ?? "");
  const { t } = useTranslation();
  return (
    <DomainListItemCard
      table={isTable}
      domain="payments"
      title={
        clientLoading ? (
          <Skeleton className="h-7 w-2/3" />
        ) : (
          (client?.name ?? "-")
        )
      }
      titleTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      description={payment.value}
      descriptionTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      editLink={(children, key) => (
        <Link
          key={key}
          to="/payments/$id/$pid"
          params={{ id: reservationId, pid: payment.id ?? "" }}
        >
          {children}
        </Link>
      )}
      contentMap={[
        {
          label: t("payments.fields.currency"),
          value: payment.currency ? (
            <T tKey={`currencies.${payment.currency}.symbol`} />
          ) : (
            "-"
          ),
        },
        {
          label: t("payments.fields.state"),
          value: payment.state ? (
            <T tKey={`payments.state.${payment.state}`} />
          ) : (
            "-"
          ),
        },
        {
          label: t("payments.fields.paymentMethod"),
          value: payment.paymentMethod ? (
            <T tKey={`payments.methods.${payment.paymentMethod}`} />
          ) : (
            "-"
          ),
        },
        {
          label: t("payments.fields.balance"),
          value: payment.balance ?? "-",
        },
        {
          label: t("payments.fields.paymentDate"),
          value: dateToString(payment.paymentDate),
        },
        {
          label: t("payments.fields.invoice"),
          value: payment.invoice ?? "-",
        },
        {
          label: t("payments.fields.invoiceUrl"),
          value: payment.invoiceUrl ? (
            <Button asChild variant="link">
              <a href={payment.invoiceUrl} target="__blank">
                {payment.invoiceUrl}
              </a>
            </Button>
          ) : (
            "-"
          ),
        },
        {
          label: t("payments.fields.notes"),
          value: payment.notes ?? "-",
          tableZoom: true,
          tableClassName:
            "max-w-36 overflow-hidden text-ellipsis whitespace-nowrap",
        },
      ]}
      onDelete={deletePayment}
    />
  );
}
